import { TriangleAlert } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  agents,
  agentImpactMessages,
  toneSurface,
} from "@/lib/mockData";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

export default function AgentSeatsCard() {
  const agentsEnabled = useStore((s) => s.agentsEnabled);
  const toggleAgent = useStore((s) => s.toggleAgent);

  const activeCount = Object.values(agentsEnabled).filter(Boolean).length;
  const fillPct = (activeCount / 8) * 100;

  return (
    <section
      data-testid="agent-seats-card"
      className="card-soft rounded-[28px] p-6 md:p-7"
    >
      <header className="mb-5">
        <h2 className="text-[18px] font-extrabold tracking-tight text-zinc-900">
          Agent Seats
        </h2>
        <p className="mt-1 text-[13px] text-zinc-500">
          Toggle agents on or off. Disabling an agent changes the boardroom's
          risk profile.
        </p>
      </header>

      <ul className="divide-y divide-black/[0.04]">
        {agents.map((a) => {
          const enabled = !!agentsEnabled[a.id];
          const tone = toneSurface[a.tone] ?? toneSurface.neutral;
          const isDark = a.tone === "dark";
          const impact = agentImpactMessages[a.id];
          return (
            <li
              key={a.id}
              data-testid={`seat-row-${a.id}`}
              data-enabled={enabled ? "true" : "false"}
              className="py-4"
            >
              <div
                className={cn(
                  "flex flex-wrap items-center gap-3 transition-opacity sm:flex-nowrap",
                  !enabled && "opacity-60",
                )}
              >
                <span
                  className={cn(
                    "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-[14px] font-extrabold",
                    isDark
                      ? "bg-[#0B0B0E] text-white"
                      : cn(tone.bg, "text-zinc-900"),
                  )}
                  style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
                >
                  {a.name[0]}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[14px] font-bold text-zinc-900">
                      {a.name}
                    </span>
                    <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-700">
                      {a.role}
                    </span>
                  </div>
                  <div className="mt-0.5 truncate text-[12.5px] text-zinc-500">
                    {a.focus}
                  </div>
                </div>
                <Switch
                  data-testid={`seat-switch-${a.id}`}
                  checked={enabled}
                  onCheckedChange={() => toggleAgent(a.id)}
                  className="ml-auto data-[state=checked]:bg-zinc-900 sm:ml-0"
                />
              </div>
              {!enabled && (
                <div
                  data-testid={`seat-impact-${a.id}`}
                  className="ml-14 mt-2 flex items-center gap-1.5 text-[12.5px] text-orange-600"
                >
                  <TriangleAlert className="h-3.5 w-3.5" strokeWidth={2.4} />
                  {impact}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <footer className="mt-5 flex items-center gap-3 border-t border-black/[0.04] pt-4">
        <span
          data-testid="seats-active-count"
          className="text-[12.5px] font-semibold text-zinc-700"
        >
          Active agents: {activeCount} of 8
        </span>
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-100">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${fillPct}%`,
              background:
                "linear-gradient(90deg, #0a0a0a 0%, #6D28D9 55%, #22D3EE 100%)",
            }}
          />
        </div>
      </footer>
    </section>
  );
}
